import {
  ExpressBusRouterBusGrade,
  ExpressBusRouterTicketType,
} from '~/api/services/express-bus-router/index.type'

export const CouponPlatformType = {
  // 일반 쿠폰
  GENERAL: 'GENERAL',
  // PLCC(NOL) 쿠폰
  PLCC: 'PLCC',
  // 	보상 쿠폰 (현재 미사용)
  REWARD: 'REWARD',
}
export type TCouponPlatformType = (typeof CouponPlatformType)[keyof typeof CouponPlatformType]

export const CouponPlatformStatus = {
  // 지급 상태
  DISTRIBUTED: 'DISTRIBUTED',
  // 사용된 상태
  REDEEMED: 'REDEEMED',
  // 	사용 취소된 상태
  UNREDEEMED: 'UNREDEEMED',
  // 회수된 상태
  WITHDREW: 'WITHDREW',
}
export type TCouponPlatformStatus = (typeof CouponPlatformStatus)[keyof typeof CouponPlatformStatus]

export const CouponPlatformInstanceAppId = {
  // 해외숙소
  GA: 'ga',
  // 레저
  Dl: 'dl',
  // 기차
  Tr: 'tr',
  // 버스
  Eb: 'eb',
}

export type TCouponPlatformInstanceAppId =
  (typeof CouponPlatformInstanceAppId)[keyof typeof CouponPlatformInstanceAppId]

export interface ICouponPlatformById {
  id: number
  couponType: TCouponPlatformType
  resource: {
    id: number
    title: string
    name: string
    description: string
  }
  discount: {
    amount: number
    percentOff: number
  }
  redemptionPolicy?: {
    conditions: {
      type: string
      path: string
      code: string
      itemSet: string[]
      value?: number
    }[]
    stock?: {
      total: number
      remain?: number
      id?: number
    }
  }
  startAt: string
  endAt: string
  status: TCouponPlatformStatus
  createdAt: string
}

interface ICouponInstancesCommonRequestData {
  memberNo: number
  /**
   * 주문 금액 (상품의 판매가)
   */
  price: number
}

/**
 * 레저 쿠폰 조회 Request Data > 레저 상품 타입 code
 */
export const CouponPlatformLeisureItemProductType = {
  LEISURE: 'LEISURE',
  RENTCAR: 'RENTCAR',
}
export type TCouponPlatformLeisureItemProductType =
  (typeof CouponPlatformLeisureItemProductType)[keyof typeof CouponPlatformLeisureItemProductType]

/**
 * 레저 쿠폰 조회 Request Data
 */
export interface ICouponInstancesLeisureRequestData extends ICouponInstancesCommonRequestData {
  items: {
    productId: string
    productType: TCouponPlatformLeisureItemProductType
  }[]
}

/**
 * 기차 쿠폰 조회 Request Data > 편도/왕복 code
 */
export const CouponPlatformTrainTicketType = {
  // 왕복
  ROUND_TRIP: 'ROUND_TRIP',
  // 편도
  ONE_WAY: 'ONE_WAY',
}
export type TCouponPlatformTrainTicketType =
  (typeof CouponPlatformTrainTicketType)[keyof typeof CouponPlatformTrainTicketType]

/**
 * 기차 쿠폰 조회 Request Data
 */
export interface ICouponInstancesTrainRequestData extends ICouponInstancesCommonRequestData {
  tickets: { trainGroupCode: string }[]
  ticketType: TCouponPlatformTrainTicketType
}

/**
 * 버스 쿠폰 조회 Request Data
 */
export interface ICouponInstancesBusRequestData extends ICouponInstancesCommonRequestData {
  reservations: {
    returnTrip: boolean
    departureAt: string
    departureTerminalNo: string
    arrivalTerminalNo: string
    busGrade: ExpressBusRouterBusGrade
    tickets: {
      ticketType: ExpressBusRouterTicketType
      price: number
    }[]
  }[]
}

/**
 * 해외주문 쿠폰 조회 Request Data
 */
export interface ICouponInstancesGlobalRequestData extends ICouponInstancesCommonRequestData {
  propertyId: string
  propertyVersion: number
  /**
   * 원 주문 금액 (회원등급제 정책 할인 전 원래 상품의 판매가)
   */
  originalPrice: number
  checkIn: string
  checkOut: string
}

export type TCouponInstancesRequestData =
  | ICouponInstancesGlobalRequestData
  | ICouponInstancesLeisureRequestData
  | ICouponInstancesTrainRequestData
  | ICouponInstancesBusRequestData

export type TCouponInstancesCondition = {
  type: string
  path: string
  code: string
  itemSet: string[]
  from?: string
  to?: string
  offset?: number
  value?: number
  operator?: string
  unit?: string
}

export const CouponPlatformCouponTarget = {
  // 고객 쿠폰
  CUSTOMER: 'CUSTOMER',
  // 상품 쿠폰
  PRODUCT: 'PRODUCT',
}
export type TCouponPlatformCouponTarget = keyof typeof CouponPlatformCouponTarget

export interface ICouponInstances {
  /**
   * 상품 쿠폰으로 인해 null 일 수 있음.
   * 고객이 소유한 쿠폰의 id
   * 상품 쿠폰의 경우 다운로드를 받아야 id 체번
   */
  id: number | null
  couponType: TCouponPlatformType
  couponTarget: TCouponPlatformCouponTarget
  /**
   * 쿠폰 관리자에 등록되는 쿠폰의 id
   */
  couponId: number
  resource: {
    id: number
    title: string
    name: string
    description: string
  }
  discount: {
    // 최대 할인 금액
    amount: number
    // 할인율. 정률 쿠폰인 경우 (1~100). 정액 쿠폰인 경우 (0)
    percentOff: number
  }
  redemptionPolicy: {
    conditions?: TCouponInstancesCondition[]
    stock?: {
      total: number
      remain?: number
      id?: number
    }
    maxUseCount?: {
      total: number
      remain: number
      id?: number
    }
  }
  startAt: string
  endAt: string
  status: TCouponPlatformStatus
  createdAt: string
  // 주어진 주문에 적용되는 쿠폰 혜택 금액 (= 적용된 쿠폰 혜택 금액)
  confirmedDiscount: number
  violations: string[]
  unavailableReasons?: string[]
}

export interface ICouponPlatformCheckProgressCouponRequestData {
  memberNo: number
  checkProgressRequests: {
    appId: TCouponPlatformInstanceAppId
    couponId: number
    conditions: {
      productId: number
      count: number
    }[]
  }[]
}

export interface ICouponPlatformCheckProgressCouponResponseData {
  appId: TCouponPlatformInstanceAppId
  couponId: number
  productIds: string[]
  title: string
  stockUsable: boolean
  maxUseCount: {
    usable: boolean
    stock?: {
      total: number
      remain: number
    }
  } | null
  couponUsable: boolean
  message: string
}

export interface ICouponPlatformCouponDownloadRequestData {
  memberNo: number
  couponId: number
  /**
   * 상품 ID
   */
  trackingCode: number
  appId: TCouponPlatformInstanceAppId
}

export interface ICouponPlatformCouponDownloadResponseData {
  id: number
  couponId: number
}
